import AdminLayout from "@/components/layout-admin";
import { useAdminSections, useCreateSection, useDeleteSection, useApartments, useBuildings } from "@/hooks/use-resources";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, FileText, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertSectionSchema, type Section, type InsertSection } from "@shared/schema";
import { z } from "zod";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

const CATEGORIES = ["check-in", "stay", "amenities", "rules"] as const;

const categoryColors: Record<string, string> = {
  "check-in": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  "stay": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  "amenities": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  "rules": "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
};

const formSchema = insertSectionSchema.extend({
  apartmentId: z.coerce.number().min(1, "Select an apartment"),
  category: z.enum(CATEGORIES, { required_error: "Select a category" }),
  titleEn: z.string().min(1, "Title (EN) is required"),
  titleEs: z.string().min(1, "Title (ES) is required"),
  order: z.coerce.number().min(0).default(0),
});

type FormValues = z.infer<typeof formSchema>;

function SectionForm({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel,
  apartments,
  buildings,
}: {
  defaultValues: FormValues;
  onSubmit: (values: FormValues) => void;
  isPending: boolean;
  submitLabel: string;
  apartments: { id: number; name: string; buildingId: number | null }[];
  buildings: { id: number; name: string }[];
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  const selectedApartmentId = form.watch("apartmentId");

  useEffect(() => {
    if (selectedApartmentId) {
      const apt = apartments.find((a) => a.id === Number(selectedApartmentId));
      if (apt?.buildingId) {
        form.setValue("buildingId", apt.buildingId);
      }
    }
  }, [selectedApartmentId, apartments]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="apartmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Apartment</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger data-testid="select-apartment">
                      <SelectValue placeholder="Select apartment" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {apartments.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id.toString()}>
                        {apt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ""}>
                  <FormControl>
                    <SelectTrigger data-testid="select-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="buildingId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Building (auto-filled)</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value?.toString() || ""}
                disabled
              >
                <FormControl>
                  <SelectTrigger data-testid="select-building">
                    <SelectValue placeholder="Auto-filled from apartment" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {buildings.map((b) => (
                    <SelectItem key={b.id} value={b.id.toString()}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="titleEn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title (EN)</FormLabel>
                <FormControl>
                  <Input data-testid="input-title-en" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="titleEs"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title (ES)</FormLabel>
                <FormControl>
                  <Input data-testid="input-title-es" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="summaryEn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Summary (EN)</FormLabel>
                <FormControl>
                  <Input
                    data-testid="input-summary-en"
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="summaryEs"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Summary (ES)</FormLabel>
                <FormControl>
                  <Input
                    data-testid="input-summary-es"
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="contentEn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content (EN)</FormLabel>
                <FormControl>
                  <Textarea
                    data-testid="textarea-content-en"
                    rows={5}
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contentEs"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content (ES)</FormLabel>
                <FormControl>
                  <Textarea
                    data-testid="textarea-content-es"
                    rows={5}
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Order</FormLabel>
              <FormControl>
                <Input
                  data-testid="input-order"
                  type="number"
                  min={0}
                  {...field}
                  value={field.value ?? 0}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={isPending}
          data-testid="button-submit-section"
        >
          {isPending ? "Saving..." : submitLabel}
        </Button>
      </form>
    </Form>
  );
}

export default function AdminContent() {
  const { data: sections, isLoading } = useAdminSections();
  const { data: apartments } = useApartments();
  const { data: buildings } = useBuildings();
  const createMutation = useCreateSection();
  const deleteMutation = useDeleteSection();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editSection, setEditSection] = useState<Section | null>(null);
  const [filterApartmentId, setFilterApartmentId] = useState<string>("all");
  const [isUpdating, setIsUpdating] = useState(false);

  const updateSection = async (id: number, data: Partial<InsertSection>) => {
    const res = await fetch(`/api/admin/sections/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error("Failed to update");
    queryClient.invalidateQueries({ queryKey: ["/api/admin/sections"] });
    return res.json();
  };

  const filteredSections = sections?.filter((s) => {
    if (filterApartmentId === "all") return true;
    return s.apartmentId === Number(filterApartmentId);
  });

  const groupedSections = filteredSections?.reduce(
    (acc, section) => {
      const aptId = section.apartmentId ?? 0;
      if (!acc[aptId]) acc[aptId] = [];
      acc[aptId].push(section);
      return acc;
    },
    {} as Record<number, Section[]>
  );

  if (groupedSections) {
    Object.values(groupedSections).forEach((group) =>
      group.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
  }

  const getApartmentName = (id: number | null) => {
    if (!id) return "Unassigned";
    return apartments?.find((a) => a.id === id)?.name || "Unknown";
  };

  async function onCreateSubmit(values: FormValues) {
    try {
      await createMutation.mutateAsync(values as InsertSection);
      toast({ title: "Section created" });
      setCreateOpen(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to create section",
        variant: "destructive",
      });
    }
  }

  async function onEditSubmit(values: FormValues) {
    if (!editSection) return;
    setIsUpdating(true);
    try {
      await updateSection(editSection.id, values as Partial<InsertSection>);
      toast({ title: "Section updated" });
      setEditSection(null);
    } catch {
      toast({
        title: "Error",
        description: "Failed to update section",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  }

  async function onDelete(id: number) {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ title: "Section deleted" });
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete section",
        variant: "destructive",
      });
    }
  }

  async function onDuplicate(section: Section, targetApartmentId?: number) {
    try {
      await createMutation.mutateAsync({
        apartmentId: targetApartmentId ?? section.apartmentId,
        buildingId: targetApartmentId
          ? (apartments?.find((a) => a.id === targetApartmentId)?.buildingId ?? section.buildingId)
          : section.buildingId,
        category: section.category,
        titleEn: section.titleEn,
        titleEs: section.titleEs,
        summaryEn: section.summaryEn || "",
        summaryEs: section.summaryEs || "",
        contentEn: section.contentEn || "",
        contentEs: section.contentEs || "",
        order: section.order ?? 0,
      } as InsertSection);
      toast({ title: "Section duplicated" });
    } catch {
      toast({ title: "Error", description: "Failed to duplicate section", variant: "destructive" });
    }
  }

  const [duplicateSection, setDuplicateSection] = useState<Section | null>(null);
  const [duplicateTargetApt, setDuplicateTargetApt] = useState<string>("same");

  const createDefaults: FormValues = {
    apartmentId: 0,
    buildingId: null,
    category: "check-in",
    titleEn: "",
    titleEs: "",
    summaryEn: "",
    summaryEs: "",
    contentEn: "",
    contentEs: "",
    order: 0,
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Content Sections
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage content for check-in, stay, amenities, and rules.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={filterApartmentId}
            onValueChange={setFilterApartmentId}
          >
            <SelectTrigger className="w-[200px]" data-testid="select-filter-apartment">
              <SelectValue placeholder="Filter by apartment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Apartments</SelectItem>
              {apartments?.map((apt) => (
                <SelectItem key={apt.id} value={apt.id.toString()}>
                  {apt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-section">
                <Plus className="w-4 h-4 mr-2" /> Add Section
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Section</DialogTitle>
              </DialogHeader>
              <SectionForm
                defaultValues={createDefaults}
                onSubmit={onCreateSubmit}
                isPending={createMutation.isPending}
                submitLabel="Create Section"
                apartments={apartments || []}
                buildings={buildings || []}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-loading">
          Loading sections...
        </div>
      )}

      {!isLoading && (!sections || sections.length === 0) && (
        <div className="text-center py-12" data-testid="text-empty">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No content sections yet. Add your first section to get started.
          </p>
        </div>
      )}

      {groupedSections &&
        Object.entries(groupedSections).map(([aptId, aptSections]) => (
          <div key={aptId} className="mb-8">
            <h2
              className="text-lg font-display font-semibold mb-4"
              data-testid={`text-apartment-group-${aptId}`}
            >
              {getApartmentName(Number(aptId))}
            </h2>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {aptSections.map((section) => (
                <Card key={section.id} data-testid={`card-section-${section.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <h3
                          className="font-display font-semibold truncate"
                          data-testid={`text-section-title-${section.id}`}
                        >
                          {section.titleEn}
                        </h3>
                        <p
                          className="text-sm text-muted-foreground truncate"
                          data-testid={`text-section-title-es-${section.id}`}
                        >
                          {section.titleEs}
                        </p>
                      </div>
                      <Badge
                        variant="secondary"
                        className={`shrink-0 no-default-hover-elevate no-default-active-elevate ${categoryColors[section.category] || ""}`}
                        data-testid={`badge-category-${section.id}`}
                      >
                        {section.category}
                      </Badge>
                    </div>

                    {section.summaryEn && (
                      <p
                        className="text-sm text-muted-foreground mb-3 line-clamp-2"
                        data-testid={`text-summary-${section.id}`}
                      >
                        {section.summaryEn}
                      </p>
                    )}

                    <div className="flex items-center justify-between gap-2 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">
                        Order: {section.order ?? 0}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setDuplicateSection(section);
                            setDuplicateTargetApt("same");
                          }}
                          data-testid={`button-duplicate-section-${section.id}`}
                          title="Duplicate"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setEditSection(section)}
                          data-testid={`button-edit-section-${section.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              data-testid={`button-delete-section-${section.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Section</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{section.titleEn}"?
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`button-cancel-delete-${section.id}`}>
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(section.id)}
                                data-testid={`button-confirm-delete-${section.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

      <Dialog
        open={!!duplicateSection}
        onOpenChange={(open) => {
          if (!open) setDuplicateSection(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Section</DialogTitle>
          </DialogHeader>
          {duplicateSection && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Duplicate "{duplicateSection.titleEn}" to another apartment or the same one.
              </p>
              <div>
                <Label className="mb-2 block">Target Apartment</Label>
                <Select value={duplicateTargetApt} onValueChange={setDuplicateTargetApt}>
                  <SelectTrigger data-testid="select-duplicate-target">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same">Same Apartment</SelectItem>
                    {apartments?.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id.toString()}>
                        {apt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full"
                disabled={createMutation.isPending}
                onClick={() => {
                  const targetId = duplicateTargetApt === "same"
                    ? undefined
                    : Number(duplicateTargetApt);
                  onDuplicate(duplicateSection, targetId);
                  setDuplicateSection(null);
                }}
                data-testid="button-confirm-duplicate"
              >
                {createMutation.isPending ? "Duplicating..." : "Duplicate Section"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editSection}
        onOpenChange={(open) => {
          if (!open) setEditSection(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Section</DialogTitle>
          </DialogHeader>
          {editSection && (
            <SectionForm
              defaultValues={{
                apartmentId: editSection.apartmentId ?? 0,
                buildingId: editSection.buildingId ?? null,
                category: editSection.category as (typeof CATEGORIES)[number],
                titleEn: editSection.titleEn,
                titleEs: editSection.titleEs,
                summaryEn: editSection.summaryEn || "",
                summaryEs: editSection.summaryEs || "",
                contentEn: editSection.contentEn || "",
                contentEs: editSection.contentEs || "",
                order: editSection.order ?? 0,
              }}
              onSubmit={onEditSubmit}
              isPending={isUpdating}
              submitLabel="Update Section"
              apartments={apartments || []}
              buildings={buildings || []}
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
